import { useEffect, useMemo } from 'react'
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import ShellPage from '@/pages/ShellPage'
import ImprintPage from '@/pages/ImprintPage'
import PrivacyPage from '@/pages/PrivacyPage'
import SandboxPage from '@/pages/SandboxPage'
import ArchivePage from '@/pages/ArchivePage'
import NisgPage from '@/pages/NisgPage'
import { I18nProvider, fromLocale } from '@/lib/i18n'
import { localePath, splitLocale } from '@/lib/seo'
import { useLinkTransitions } from '@/lib/transitions'

export default function App() {
  const location = useLocation()
  const navigate = useNavigate()
  // German lives at /…, English at /en/… (seo.ts). The routes below only
  // see the unprefixed path, so /services and /en/services render the same tree
  // and a language switch swaps the copy without remounting the page.
  const { locale, path } = splitLocale(location.pathname)
  const routed = useMemo(() => ({ ...location, pathname: path }), [location, path])
  useLinkTransitions(navigate)

  // Old links used `?lang=EN|DE` on the bare URL; send them to the
  // language's own URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const legacy = params.get('lang')?.toLowerCase()
    if (!legacy) return
    params.delete('lang')
    const search = params.toString()
    const target = legacy === 'en' || legacy === 'de' ? localePath(path, legacy) : location.pathname
    navigate({ pathname: target, search: search ? `?${search}` : '', hash: location.hash }, { replace: true })
  }, [location, path, navigate])

  return (
    <I18nProvider lang={fromLocale(locale)} path={path} onNavigate={navigate}>
      <Routes location={routed}>
        <Route path="/imprint" element={<ImprintPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/demo/:project" element={<SandboxPage />} />
        <Route path="/archive/*" element={<ArchivePage />} />
        <Route path="/nisg-2026" element={<NisgPage />} />
        {/* Home + section routes (/services, /projects, /contact, /faq) all render
            the single-page shell, which derives the active section from the URL
            and handles in-page smooth-scroll navigation itself. Any other path
            gets the 404 page (ShellPage decides, so moving between sections
            never remounts the shell). */}
        <Route path="*" element={<ShellPage />} />
      </Routes>
    </I18nProvider>
  )
}
